import { useEffect, useRef, useState } from 'react';

export interface UseInViewOptions {
  /** Fraction of element visible (0–1). Default 0.2 */
  threshold?: number;
  /** CSS margin around root. Default '0px' */
  rootMargin?: string;
  /** Unobserve after first time in view (stops observing). Default false */
  unobserveOnce?: boolean;
}

/**
 * Returns a ref to attach to an element and whether that element is in view.
 * Uses IntersectionObserver; falls back to setting inView true when observer is unavailable.
 */
export function useInView(
  options: UseInViewOptions = {}
): [ref: React.RefObject<HTMLElement>, inView: boolean] {
  const {
    threshold = 0.2,
    rootMargin = '0px',
    unobserveOnce = false,
  } = options;

  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let cancelled = false;

    if (typeof IntersectionObserver === 'undefined') {
      if (typeof queueMicrotask === 'function') {
        queueMicrotask(() => {
          if (!cancelled) setInView(true);
        });
      } else {
        Promise.resolve().then(() => {
          if (!cancelled) setInView(true);
        });
      }
      return () => {
        cancelled = true;
      };
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (cancelled || !entry?.isIntersecting) return;
        setInView(true);
        if (unobserveOnce) observer.unobserve(el);
      },
      { threshold, rootMargin }
    );
    observer.observe(el);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [threshold, rootMargin, unobserveOnce]);

  return [ref, inView];
}
